<script lang="ts">
  import '../styles/app.css';
  import { onMount } from 'svelte';
  import { page } from '$app/stores';
  import { goto } from '$app/navigation';
  import type { Snippet } from 'svelte';
  import { initAuth, auth, signOut } from '$lib/auth.svelte';
  import { initDatabase } from '$lib/db';
  import { initWorkspaces, resetWorkspaces, workspace } from '$lib/workspace.svelte';
  import { initActivities, resetActivities } from '$lib/activity.svelte';
  import { initCategories, resetCategories } from '$lib/category.svelte';
  import WorkspaceSwitcher from '$lib/components/WorkspaceSwitcher.svelte';

  /**
   * Root Layout — shell aplikasi EKINKINIAN.
   *
   * Tanggung jawab:
   * 1. Mengimpor global CSS.
   * 2. Mengelola tema (light/dark).
   * 3. Menginisialisasi auth listener (initAuth).
   * 4. Route guard: redirect ke /login jika belum terautentikasi,
   *    redirect ke / jika sudah login tapi berada di /login (UC-01, FR-AUTH-06).
   * 5. Merender topbar persisten (dengan info user + logout) + area konten.
   *
   * Halaman /login dirender standalone (tanpa topbar) karena route publik.
   */

  interface Props {
    children: Snippet;
  }

  let { children }: Props = $props();

  // --- Tema ---
  type Theme = 'light' | 'dark';

  function getInitialTheme(): Theme {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('ekn-theme');
      if (stored === 'light' || stored === 'dark') return stored;
    }
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      return 'dark';
    }
    return 'light';
  }

  let theme = $state<Theme>(getInitialTheme());

  function applyTheme(value: Theme) {
    document.documentElement.setAttribute('data-theme', value);
    localStorage.setItem('ekn-theme', value);
  }

  $effect(() => {
    applyTheme(theme);
  });

  function toggleTheme() {
    theme = theme === 'light' ? 'dark' : 'light';
  }

  // --- Auth ---

  /** Cek apakah path saat ini adalah route login (publik). */
  function isLoginPath(pathname: string): boolean {
    return pathname === '/login' || pathname === '/login/';
  }

  let signingOut = $state(false);
  let dbReady = $state(false);

  onMount(() => {
    // Inisialisasi listener auth sekali saat root mount.
    initAuth();
    // Inisialisasi SQLite cache (offline-first). Berjalan paralel dengan auth.
    // Jika gagal (browser tidak support WASM), app tetap berfungsi tanpa cache.
    void initDatabase()
      .then(() => {
        dbReady = true;
      })
      .catch((err) => {
        console.error('[db] Failed to initialize SQLite cache:', err);
      });
  });

  // Inisialisasi workspace store saat user terautentikasi dan DB siap (FR-WS-01).
  // Reset state saat user logout agar tidak ada data user sebelumnya tertinggal.
  $effect(() => {
    if (auth.user && dbReady && !workspace.isInitialized) {
      void initWorkspaces(auth.user.id);
    }
    if (!auth.isAuthenticated && workspace.isInitialized) {
      resetWorkspaces();
      resetActivities();
      resetCategories();
    }
  });

  // Inisialisasi activity & category stores saat workspace aktif tersedia (S8).
  // Reset saat workspace aktif berubah (switch) agar data tidak tercampur.
  $effect(() => {
    if (workspace.activeWorkspace && workspace.activeWorkspaceId) {
      const ws = workspace.activeWorkspace;
      void initActivities(ws.id, ws.tenant_id);
      void initCategories(ws.id, ws.tenant_id);
    }
  });

  // Route guard: berjalan reaktif terhadap loading, isAuthenticated, dan path.
  $effect(() => {
    const currentPath = $page.url.pathname;
    const isLoginRoute = isLoginPath(currentPath);

    // Tunggu sampai state auth selesai dimuat.
    if (auth.loading) return;

    if (!auth.isAuthenticated && !isLoginRoute) {
      // Belum login + bukan di /login → arahkan ke login (FR-AUTH-06).
      void goto('/login', { replaceState: true });
    } else if (auth.isAuthenticated && isLoginRoute) {
      // Sudah login + di /login → arahkan ke dashboard.
      void goto('/', { replaceState: true });
    }
  });

  async function handleSignOut() {
    signingOut = true;
    try {
      await signOut();
      // onAuthStateChange akan memicu route guard → redirect ke /login.
    } catch {
      // Abaikan error; tetap di halaman saat ini.
    } finally {
      signingOut = false;
    }
  }
</script>

{#if auth.loading}
  <!-- State auth belum selesai dimuat: tampilkan spinner full-page. -->
  <div class="auth-loading">
    <span class="loading-spinner" aria-label="Memuat"></span>
  </div>
{:else if isLoginPath($page.url.pathname)}
  <!-- Route publik: halaman login dirender standalone tanpa app shell. -->
  {@render children()}
{:else if auth.isAuthenticated}
  <div class="app-shell">
    <header class="topbar">
      <div class="topbar-left">
        <div class="topbar-brand">
          <img src="/logo.png" alt="EKINKINIAN" class="brand-logo" width="36" height="36" />
          <div class="brand-text">
            <span class="brand-name">EKINKINIAN</span>
            <span class="brand-tagline">E-Kinerja Kini Lebih Nyaman</span>
          </div>
        </div>
        <WorkspaceSwitcher />
      </div>

      <div class="topbar-actions">
        {#if auth.userEmail}
          <span class="user-email" title={auth.userEmail}>{auth.userEmail}</span>
        {/if}
        <button class="theme-toggle" onclick={toggleTheme} aria-label="Ganti tema">
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
        <button
          class="logout-btn"
          onclick={handleSignOut}
          disabled={signingOut}
          aria-label="Keluar"
        >
          {signingOut ? '…' : 'Keluar'}
        </button>
      </div>
    </header>

    <main class="app-content">
      {@render children()}
    </main>
  </div>
{/if}

<style>
  .app-shell {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .topbar {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--ekn-space-6);
    background-color: var(--ekn-color-bg-elevated);
    border-bottom: var(--ekn-border-1) solid var(--ekn-color-border-default);
    box-shadow: var(--ekn-shadow-sm);
  }

  .topbar-left {
    display: flex;
    align-items: center;
    gap: var(--ekn-space-4);
  }

  .topbar-brand {
    display: flex;
    align-items: center;
    gap: var(--ekn-space-3);
  }

  .brand-logo {
    width: 36px;
    height: 36px;
    border-radius: var(--ekn-radius-md);
    object-fit: contain;
    flex-shrink: 0;
  }

  .brand-text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }

  .brand-name {
    font-weight: var(--ekn-font-bold);
    font-size: var(--ekn-text-base);
    color: var(--ekn-color-text-primary);
    letter-spacing: 0.02em;
  }

  .brand-tagline {
    font-size: var(--ekn-text-xs);
    color: var(--ekn-color-text-secondary);
  }

  .topbar-actions {
    display: flex;
    align-items: center;
    gap: var(--ekn-space-3);
  }

  .user-email {
    font-size: var(--ekn-text-sm);
    color: var(--ekn-color-text-secondary);
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .theme-toggle {
    font-size: var(--ekn-text-xl);
    padding: var(--ekn-space-2);
    border-radius: var(--ekn-radius-md);
    transition: background-color var(--ekn-transition-fast);
  }
  .theme-toggle:hover {
    background-color: var(--ekn-color-bg-hover);
  }

  .logout-btn {
    font-family: var(--ekn-font-sans);
    font-size: var(--ekn-text-sm);
    font-weight: var(--ekn-font-medium);
    color: var(--ekn-color-danger);
    padding: var(--ekn-space-2) var(--ekn-space-3);
    border-radius: var(--ekn-radius-md);
    border: var(--ekn-border-1) solid var(--ekn-color-border-default);
    transition: background-color var(--ekn-transition-fast);
  }
  .logout-btn:hover:not(:disabled) {
    background-color: color-mix(in srgb, var(--ekn-color-danger) 10%, transparent);
  }
  .logout-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .app-content {
    flex: 1;
  }

  /* --- Auth loading state --- */
  .auth-loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--ekn-color-bg-base);
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--ekn-color-border-default);
    border-top-color: var(--ekn-color-action-primary);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 640px) {
    .topbar {
      padding: 0 var(--ekn-space-4);
    }
    .topbar-left {
      gap: var(--ekn-space-2);
    }
    .brand-tagline {
      display: none;
    }
    .user-email {
      display: none;
    }
  }
</style>
